/**
 * Состояние экрана в адресе строки браузера — §14.4 ТЗ.
 *
 * > Прямые ссылки: состояние фильтров в URL, чтобы бот мог давать точную
 * > ссылку.
 *
 * Это не удобство и не мода на маршрутизацию. Бот присылает сообщение
 * «выручка за субботу — 187 400 ₽» и кнопку «открыть в дашборде»; ссылка
 * обязана открыть **ту же субботу и тот же разрез**. Ссылка, открывающая
 * умолчание, порождает спор о цифрах на ровном месте: два человека
 * смотрят на разное и оба уверены, что смотрят на одно.
 *
 * **Своего маршрутизатора нет и не нужно.** Экранов пять, вложенности
 * никакой, а `react-router` — это ещё одна зависимость, которую заказчику
 * придётся сопровождать (D5). `URLSearchParams` и `history.pushState`
 * решают ту же задачу тридцатью строками, и эти тридцать строк здесь
 * целиком видны.
 *
 * **`pushState`, а не `replaceState`.** Кнопка «назад» браузера обязана
 * возвращать к предыдущему разрезу: человек, провалившийся из выручки в
 * категорию, а оттуда в чеки, ждёт, что «назад» вернёт его в категорию, а
 * не выбросит из дашборда.
 */

import { useCallback, useEffect, useState } from 'react';

export interface UrlState {
  screen: string;
  from?: string;
  to?: string;
  grain?: string;
  waiter?: string;
  category?: string;
  item?: string;
}

const DEFAULT_SCREEN = 'overview';

function read(): UrlState {
  if (typeof window === 'undefined') return { screen: DEFAULT_SCREEN };
  const params = new URLSearchParams(window.location.search);
  const state: UrlState = { screen: params.get('screen') || DEFAULT_SCREEN };
  for (const key of ['from', 'to', 'grain', 'waiter', 'category', 'item'] as const) {
    const value = params.get(key);
    if (value) state[key] = value;
  }
  return state;
}

function write(state: UrlState): void {
  const params = new URLSearchParams();
  // Экран пишется всегда, даже умолчанием: ссылка без него выглядит
  // незаконченной, и человек, копирующий её из адресной строки, не
  // уверен, что скопировал всё.
  params.set('screen', state.screen);
  for (const key of ['from', 'to', 'grain', 'waiter', 'category', 'item'] as const) {
    const value = state[key];
    if (value) params.set(key, value);
  }
  const next = `${window.location.pathname}?${params.toString()}`;
  if (next !== `${window.location.pathname}${window.location.search}`) {
    window.history.pushState(state, '', next);
  }
}

export function useUrlState(): [UrlState, (patch: Partial<UrlState>) => void] {
  const [state, setState] = useState<UrlState>(read);

  // Кнопки «назад» и «вперёд» браузера меняют адрес мимо нас — состояние
  // читается заново. Без этого дашборд после «назад» показывал бы
  // прежний экран при изменившемся адресе, то есть врал бы ссылкой.
  useEffect(() => {
    const onPop = () => setState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const update = useCallback((patch: Partial<UrlState>) => {
    setState((previous) => {
      const next = { ...previous, ...patch };
      // `undefined` в патче означает «убрать из адреса»: снятый фильтр
      // должен исчезнуть из ссылки, а не остаться в ней пустым.
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === '') {
          delete next[key as keyof UrlState];
        }
      }
      write(next);
      return next;
    });
  }, []);

  return [state, update];
}
